import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

/**
 * A single color stop used when building a gradient recolor.
 * - `offset`: 0..1 position along the gradient (left = 0, right = 1).
 * - `color`: any valid CSS color (hex, rgb, rgba, named, ...).
 */
export interface SvgGradientStop {
  offset: number;
  color: string;
}

/**
 * Recolor configuration. Either provide a `color` for a solid recolor,
 * or a list of `gradientStops` (>= 2) for a left-to-right linear gradient.
 */
export type SvgRecolorOptions =
  | { color: string; gradientStops?: never }
  | { color?: never; gradientStops: SvgGradientStop[] };

@Injectable({
  providedIn: 'root'
})
export class SvgRecolorService {

  private readonly rawSvgCache = new Map<string, Observable<string>>();
  private gradientIdCounter = 0;

  private readonly http = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);

  /**
   * Load an SVG file from the assets folder (or any reachable URL),
   * recolor it according to the given options and return the resulting
   * SVG markup as a string.
   */
  public recolor(
    assetUrl: string,
    options: SvgRecolorOptions
  ): Observable<string> {
    return this.loadRawSvg(assetUrl).pipe(
      map(rawSvg => this.applyRecolor(rawSvg, options))
    );
  }

  /**
   * Same as `recolor` but returns a `SafeHtml` value, ready to be bound
   * to `[innerHTML]` in a template.
   */
  public recolorAsSafeHtml(
    assetUrl: string,
    options: SvgRecolorOptions
  ): Observable<SafeHtml> {
    return this.recolor(assetUrl, options).pipe(
      map(svg => this.sanitizer.bypassSecurityTrustHtml(svg))
    );
  }

  /**
   * Load an SVG file untouched (cached). For icons that carry their own
   * theming via `currentColor` / CSS custom properties.
   */
  public load(assetUrl: string): Observable<string> {
    return this.loadRawSvg(assetUrl);
  }

  /**
   * Recolor an SVG string that is already in memory (no network load).
   */
  public recolorMarkup(
    svgMarkup: string,
    options: SvgRecolorOptions
  ): string {
    return this.applyRecolor(svgMarkup, options);
  }

  private loadRawSvg(assetUrl: string): Observable<string> {
    const cached = this.rawSvgCache.get(assetUrl);
    if (cached) {
      return cached;
    }

    const request$ = this.http.get(assetUrl, { responseType: 'text' }).pipe(
      shareReplay(1),
      tap({
        error: () => this.rawSvgCache.delete(assetUrl)
      })
    );

    this.rawSvgCache.set(assetUrl, request$);
    return request$;
  }

  private applyRecolor(rawSvg: string, options: SvgRecolorOptions): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawSvg, 'image/svg+xml');

    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      throw new Error('SvgRecolorService: failed to parse SVG markup.');
    }

    const svgElement = doc.documentElement;
    if (!svgElement || svgElement.nodeName.toLowerCase() !== 'svg') {
      throw new Error('SvgRecolorService: root element is not <svg>.');
    }

    if ('color' in options && options.color !== undefined) {
      this.applySolidColor(svgElement, options.color);
    } else if ('gradientStops' in options && options.gradientStops) {
      this.applyGradient(svgElement, options.gradientStops);
    } else {
      throw new Error(
        'SvgRecolorService: provide either `color` or `gradientStops`.'
      );
    }

    return new XMLSerializer().serializeToString(svgElement);
  }

  private applySolidColor(svg: Element, color: string): void {
    this.recolorPaintedNodes(svg, color);
  }

  private applyGradient(svg: Element, stops: SvgGradientStop[]): void {
    if (stops.length < 2) {
      throw new Error(
        'SvgRecolorService: gradient requires at least two stops.'
      );
    }

    const svgNs = 'http://www.w3.org/2000/svg';
    const doc = svg.ownerDocument;
    const gradientId = `svg-recolor-gradient-${++this.gradientIdCounter}`;

    let defs = svg.querySelector(':scope > defs');
    if (!defs) {
      defs = doc.createElementNS(svgNs, 'defs');
      svg.insertBefore(defs, svg.firstChild);
    }

    const gradient = doc.createElementNS(svgNs, 'linearGradient');
    gradient.setAttribute('id', gradientId);
    // Left to right across the SVG bounding box.
    gradient.setAttribute('x1', '0%');
    gradient.setAttribute('y1', '0%');
    gradient.setAttribute('x2', '100%');
    gradient.setAttribute('y2', '0%');
    gradient.setAttribute('gradientUnits', 'objectBoundingBox');

    for (const stop of stops) {
      const stopEl = doc.createElementNS(svgNs, 'stop');
      const clamped = Math.max(0, Math.min(1, stop.offset));
      stopEl.setAttribute('offset', `${clamped * 100}%`);
      stopEl.setAttribute('stop-color', stop.color);
      gradient.appendChild(stopEl);
    }

    defs.appendChild(gradient);

    this.recolorPaintedNodes(svg, `url(#${gradientId})`);
  }

  /**
   * Walk the SVG tree and replace every non-`none` fill/stroke with the
   * given paint value. Elements that explicitly have `fill="none"` or
   * `stroke="none"` are left untouched, so outlines remain outlines.
   */
  private recolorPaintedNodes(root: Element, paint: string): void {
    const paintedTags = new Set([
      'path', 'rect', 'circle', 'ellipse', 'line',
      'polyline', 'polygon', 'text', 'tspan', 'g', 'use'
    ]);

    const walker = (node: Element): void => {
      const tag = node.nodeName.toLowerCase();

      if (tag === 'style') {
        this.replacePaintInStyleSheet(node, paint);
      } else if (paintedTags.has(tag)) {
        this.replacePaint(node, 'fill', paint);
        this.replacePaint(node, 'stroke', paint);
        this.replacePaintInStyle(node, paint);
      }

      for (let i = 0; i < node.children.length; i++) {
        walker(node.children[i]);
      }
    };

    walker(root);
  }

  /**
   * Rewrite `fill:` / `stroke:` declarations inside an inline SVG `<style>`
   * element. CSS rules from `<style>` win over presentation attributes, so
   * leaving them untouched would override our recolor.
   */
  private replacePaintInStyleSheet(styleNode: Element, paint: string): void {
    const css = styleNode.textContent;
    if (!css) {
      return;
    }

    const updated = css.replace(
      /(fill|stroke)\s*:\s*([^;}]+)/gi,
      (match, prop: string, value: string) => {
        const trimmed = value.trim().toLowerCase();
        if (trimmed === 'none' || trimmed === 'inherit' ||
            trimmed === 'currentcolor' || trimmed === 'transparent') {
          return match;
        }
        return `${prop}:${paint}`;
      }
    );

    styleNode.textContent = updated;
  }

  private replacePaint(
    node: Element,
    attr: 'fill' | 'stroke',
    paint: string
  ): void {
    const current = node.getAttribute(attr);

    if (current === 'none') {
      return;
    }

    // Only override stroke if it was actually set (so we don't add strokes
    // to shapes that never had one). Fill defaults to black when missing,
    // so we set it explicitly to take over the default too.
    if (attr === 'stroke' && current === null) {
      return;
    }

    node.setAttribute(attr, paint);
  }

  private replacePaintInStyle(node: Element, paint: string): void {
    const style = node.getAttribute('style');
    if (!style) {
      return;
    }

    const updated = style
      .split(';')
      .map(decl => decl.trim())
      .filter(decl => decl.length > 0)
      .map(decl => {
        const [propRaw, ...valueParts] = decl.split(':');
        const prop = propRaw.trim().toLowerCase();
        const value = valueParts.join(':').trim();

        if ((prop === 'fill' || prop === 'stroke') && value !== 'none') {
          return `${prop}:${paint}`;
        }
        return `${prop}:${value}`;
      })
      .join(';');

    node.setAttribute('style', updated);
  }
}
